import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { User, Mail, Phone, UserCheck, MapPin, FileText, Stethoscope, Building, CheckCircle, AlertCircle } from 'lucide-react';
import { api } from '../api/greenclub';
const PublicRegister = () => {
    const [formData, setFormData] = useState({
        nome: '',
        email: '',
        telefone: '',
        tipo: 'medico'
    }) ;
    const [loading, setLoading] = useState(false) ;
    const [submitted, setSubmitted] = useState(false) ;
    const [error, setError] = useState('') ;
    const handleSubmit = async (e) => {
        e.preventDefault() ;
        setLoading(true) ;
        setError('') ;
        try {
            // Validate required fields
            if (!formData.nome || !formData.email || !formData.telefone) {
                setError('Por favor, preencha todos os campos obrigatórios') ;
                return;
            }
            if (formData.tipo === 'medico' && !formData.crm) {
                setError('CRM é obrigatório para médicos') ;
                return;
            }
            if (formData.tipo === 'representante' && !formData.territorio) {
                setError('Território é obrigatório para representantes') ;
                return;
            }
            const response = await api.createPublicRegistration(formData) ;
            if (response.success) {
                setSubmitted(true) ;
            }
            else {
                setError(response.error || 'Erro ao enviar solicitação') ;
            }
        }
        catch (error) {
            console.error('Erro ao enviar cadastro:', error) ;
            setError('Erro ao enviar solicitação. Tente novamente.') ;
        }
        finally {
            setLoading(false) ;
        }
    };
    const handleChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        })) ;
    };
    if (submitted) {
        return (<motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-emerald-600"/>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Solicitação Enviada!
          </h2>
          <p className="text-gray-600 mb-6">
            Sua solicitação de cadastro foi enviada com sucesso. Você receberá um email quando for aprovada pelo administrador.
          </p>
          <button onClick={() => {
                setSubmitted(false) ;
                setFormData({
                    nome: '',
                    email: '',
                    telefone: '',
                    tipo: 'medico'
                }) ;
            }} className="w-full bg-emerald-600 text-white py-3 rounded-lg font-medium hover:bg-emerald-700 transition-colors">
            Fazer Nova Solicitação
          </button>
        </div>
      </motion.div>) ;
    }
    return (<motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-100 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-8 text-white">
            <h1 className="text-3xl font-bold mb-2">Cadastre-se no GreenClub</h1>
            <p className="text-emerald-100">
              Solicite seu cadastro como médico ou representante e faça parte da nossa rede de cannabis medicinal
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-8 space-y-6">
            {/* Error Message */}
            {error && (<div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0"/>
                <p className="text-red-800 text-sm">{error}</p>
              </div>)}

            {/* User Type Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Tipo de Cadastro
              </label>
              <div className="grid grid-cols-2 gap-4">
                <button type="button" onClick={() => handleChange('tipo', 'medico')} className={`p-4 border-2 rounded-xl text-center transition-all ${formData.tipo === 'medico'
            ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
            : 'border-gray-200 hover:border-gray-300'}`}>
                  <Stethoscope className="w-8 h-8 mx-auto mb-2"/>
                  <p className="font-medium">Médico</p>
                  <p className="text-xs text-gray-600">Prescrever cannabis medicinal</p>
                </button>
                <button type="button" onClick={() => handleChange('tipo', 'representante')} className={`p-4 border-2 rounded-xl text-center transition-all ${formData.tipo === 'representante'
            ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
            : 'border-gray-200 hover:border-gray-300'}`}>
                  <Building className="w-8 h-8 mx-auto mb-2"/>
                  <p className="font-medium">Representante</p>
                  <p className="text-xs text-gray-600">Acompanhar vendas médicas</p>
                </button>
              </div>
            </div>

            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nome Completo *
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5"/>
                  <input type="text" value={formData.nome} onChange={(e) => handleChange('nome', e.target.value)} className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent" placeholder="Seu nome completo" required/>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5"/>
                  <input type="email" value={formData.email} onChange={(e) => handleChange('email', e.target.value)} className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent" placeholder="seu@email.com" required/>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Telefone *
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5"/>
                <input type="tel" value={formData.telefone} onChange={(e) => handleChange('telefone', e.target.value)} className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent" placeholder="(11) 99999-9999" required/>
              </div>
            </div>

            {/* Médico specific fields */}
            {formData.tipo === 'medico' && (<div className="space-y-4 p-4 bg-emerald-50 rounded-lg">
                <h3 className="font-medium text-emerald-800 flex items-center">
                  <Stethoscope className="w-4 h-4 mr-2"/>
                  Informações Médicas
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      CRM *
                    </label>
                    <input type="text" value={formData.crm || ''} onChange={(e) => handleChange('crm', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent" placeholder="CRM/SP 123456" required={formData.tipo === 'medico'}/>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Especialidade
                    </label>
                    <input type="text" value={formData.especialidade || ''} onChange={(e) => handleChange('especialidade', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent" placeholder="Ex: Neurologia, Oncologia"/>
                  </div>
                </div>
              </div>)}

            {/* Representante specific fields */}
            {formData.tipo === 'representante' && (<div className="space-y-4 p-4 bg-blue-50 rounded-lg">
                <h3 className="font-medium text-blue-800 flex items-center">
                  <MapPin className="w-4 h-4 mr-2"/>
                  Informações do Representante
                </h3>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Território de Atuação *
                  </label>
                  <input type="text" value={formData.territorio || ''} onChange={(e) => handleChange('territorio', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="Ex: São Paulo, Rio de Janeiro, Região Sul" required={formData.tipo === 'representante'}/>
                </div>
              </div>)}

            {/* Observações */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Observações Adicionais
              </label>
              <div className="relative">
                <FileText className="absolute left-3 top-3 text-gray-400 w-5 h-5"/>
                <textarea value={formData.observacoes || ''} onChange={(e) => handleChange('observacoes', e.target.value)} rows={4} className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none" placeholder="Conte mais sobre sua experiência ou motivação para se juntar ao GreenClub..."/>
              </div>
            </div>

            {/* Submit Button */}
            <div>
              <button type="submit" disabled={loading} className="w-full bg-emerald-600 text-white py-4 rounded-lg font-medium hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2">
                {loading ? (<div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>) : (<>
                    <UserCheck className="w-5 h-5"/>
                    <span>Enviar Solicitação</span>
                  </>)}
              </button>
            </div>

            <div className="text-center text-sm text-gray-600">
              <p>
                Ao enviar sua solicitação, você concorda que suas informações serão analisadas pela nossa equipe.
                Você receberá um email com o resultado da análise.
              </p>
            </div>
          </form>
        </div>
      </div>
    </motion.div>) ;
};
export default PublicRegister;
